import { useEffect, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, Film, Plus, Trash2, Send, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatWIT } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/lib/adminConst";

export default function MediaLibrary() {
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [units, setUnits] = useState([]);
  const [uploading, setUploading] = useState(false);

  const isPublisher = ["super_admin", "publisher"].includes(user?.role);

  const load = () => {
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
    api.get("/galleries").then((r) => setGalleries(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/public/units").then((r) => setUnits(r.data)); }, []);

  const upload = async (file, isInternal = false) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("purpose", "image"); fd.append("is_internal", isInternal);
    try { await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Terunggah"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setUploading(false); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-extrabold text-dlh-green mb-6">Media & Galeri</h1>
      <Tabs defaultValue="media">
        <TabsList className="mb-6">
          <TabsTrigger value="media"><ImageIcon className="h-4 w-4 mr-2" />Pustaka Media</TabsTrigger>
          <TabsTrigger value="galeri"><Film className="h-4 w-4 mr-2" />Album Galeri</TabsTrigger>
        </TabsList>

        <TabsContent value="media">
          <div className="flex gap-3 mb-6">
            <label className="inline-flex items-center gap-2 bg-dlh-green text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-dlh-greenDark" data-testid="media-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Unggah Foto/Dokumen
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            </label>
          </div>
          {media.length === 0 ? <Empty text="Belum ada media." /> : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {media.map((m) => (
                <div key={m.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
                  <div className="aspect-square bg-gray-100">
                    {m.ext === "pdf" ? <div className="flex items-center justify-center h-full text-gray-400 text-xs">PDF</div> : <img src={mediaUrl(m.url)} alt={m.original_filename} loading="lazy" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-2">
                    <p className="text-[11px] text-gray-600 truncate">{m.original_filename}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-[9px] px-1 rounded ${m.access === "public" ? "bg-green-100 text-green-700" : m.access === "internal" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}>{m.access}</span>
                      <button onClick={() => { navigator.clipboard.writeText(m.url); toast.success("URL disalin"); }} className="text-gray-400 hover:text-dlh-green"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="galeri">
          <GalleryManager galleries={galleries} units={units} media={media} isPublisher={isPublisher} onChange={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GalleryManager({ galleries, units, media, isPublisher, onChange }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ type: "photo", title: "", unit: "", event_date: "", description: "", items: [] });

  const create = async () => {
    if (!draft.title) { toast.error("Judul album wajib"); return; }
    try { await api.post("/galleries", draft); toast.success("Album dibuat"); setCreating(false); setDraft({ type: "photo", title: "", unit: "", event_date: "", description: "", items: [] }); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const publish = async (id) => { try { await api.post(`/galleries/${id}/publish`); toast.success("Album diterbitkan"); onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };
  const remove = async (id) => { try { await api.delete(`/galleries/${id}`); toast.success("Diarsipkan"); onChange(); } catch (e) { toast.error("Gagal"); } };

  return (
    <div>
      <Button onClick={() => setCreating((v) => !v)} className="bg-dlh-green hover:bg-dlh-greenDark mb-4"><Plus className="h-4 w-4 mr-1" /> Album Baru</Button>
      {creating && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value, items: [] })} className="border rounded-lg px-3 py-2"><option value="photo">Foto</option><option value="video">Video</option></select>
            <select value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} className="border rounded-lg px-3 py-2"><option value="">Unit (opsional)</option>{units.map((u) => <option key={u.slug} value={u.slug}>{u.short_name}</option>)}</select>
          </div>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Judul album" className="w-full border rounded-lg px-3 py-2" data-testid="gallery-title" />
          <input type="date" value={draft.event_date} onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
          <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Deskripsi" rows={2} className="w-full border rounded-lg px-3 py-2" />
          {draft.type === "photo" ? (
            <div>
              <p className="text-xs text-gray-500 mb-2">Pilih foto dari pustaka media:</p>
              <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto">
                {media.filter((m) => m.ext !== "pdf").map((m) => {
                  const sel = draft.items.some((it) => it.media_id === m.id);
                  return <button key={m.id} onClick={() => setDraft({ ...draft, items: sel ? draft.items.filter((it) => it.media_id !== m.id) : [...draft.items, { media_id: m.id, url: m.url, caption: "" }] })} className={`aspect-square rounded overflow-hidden border-2 ${sel ? "border-dlh-orange" : "border-transparent"}`}><img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" /></button>;
                })}
              </div>
            </div>
          ) : (
            <textarea onChange={(e) => setDraft({ ...draft, items: e.target.value.split("\n").filter(Boolean).map((u) => ({ url: u.trim(), title: "" })) })} placeholder="Tautan video (YouTube/Vimeo), satu per baris" rows={3} className="w-full border rounded-lg px-3 py-2" />
          )}
          <Button onClick={create} className="bg-dlh-green hover:bg-dlh-greenDark">Simpan Album</Button>
        </div>
      )}

      {galleries.length === 0 ? <Empty text="Belum ada album." /> : (
        <div className="space-y-3">
          {galleries.map((g) => (
            <div key={g.id} className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
              <div>
                <p className="font-semibold text-dlh-green">{g.title} <span className="text-xs text-gray-400">({g.type})</span></p>
                <p className="text-xs text-gray-500">{(g.items || []).length} item · {g.event_date ? formatWIT(g.event_date) : "—"}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={g.status} />
                {isPublisher && g.status !== "published" && <button onClick={() => publish(g.id)} className="p-2 rounded hover:bg-dlh-greenPale text-dlh-green" title="Terbitkan"><Send className="h-4 w-4" /></button>}
                {isPublisher && <button onClick={() => remove(g.id)} className="p-2 rounded hover:bg-red-50 text-red-500" title="Arsipkan"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) { return <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-400">{text}</div>; }
